import { DRAFTS_VERSION } from './compose-panel.constants';
import { stripHtml } from './compose-panel.helpers';
import { IComposePanelFields } from './compose-panel.types';

export interface IStoredDraft {
  id: string;
  savedAt: number;
  values: IComposePanelFields;
}

interface IDraftsEnvelope {
  v: number;
  drafts: IStoredDraft[];
}

export const draftsStorageKey = (companyId: string | undefined, userId: string | undefined) =>
  `mea:adHocCommDrafts:${companyId || 'none'}:${userId || 'anon'}`;

export const createDraftId = (): string =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

// Newest first. Returns [] on a missing/corrupt/version-mismatched store.
export const loadDrafts = (storageKey: string): IStoredDraft[] => {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as IDraftsEnvelope;
    if (!parsed || parsed.v !== DRAFTS_VERSION || !Array.isArray(parsed.drafts)) return [];
    return [...parsed.drafts].sort((a, b) => b.savedAt - a.savedAt);
  } catch {
    return [];
  }
};

const writeDrafts = (storageKey: string, drafts: IStoredDraft[]): IStoredDraft[] => {
  const sorted = [...drafts].sort((a, b) => b.savedAt - a.savedAt);
  try {
    const envelope: IDraftsEnvelope = { v: DRAFTS_VERSION, drafts: sorted };
    window.localStorage.setItem(storageKey, JSON.stringify(envelope));
  } catch {
    // Storage quota / private mode — caller still gets the in-memory list.
  }
  return sorted;
};

export const upsertDraft = (storageKey: string, draft: IStoredDraft): IStoredDraft[] => {
  const others = loadDrafts(storageKey).filter((d) => d.id !== draft.id);
  return writeDrafts(storageKey, [draft, ...others]);
};

export const removeDraft = (storageKey: string, id: string): IStoredDraft[] =>
  writeDrafts(storageKey, loadDrafts(storageKey).filter((d) => d.id !== id));

export const isDraftNonEmpty = (values: IComposePanelFields): boolean =>
  !!(values.title || values.subject || values.bodyHtml || values.smsBody);

export const draftLabel = (values: IComposePanelFields): string =>
  values.title.trim() || values.subject.trim() || 'Untitled draft';

export const draftPreview = (values: IComposePanelFields): string => {
  const text = stripHtml(values.bodyHtml) || (values.smsBody || '').trim();
  return text.length > 120 ? `${text.slice(0, 120)}…` : text;
};
